import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiClient, ApiError, type AuthTokens } from '@clientma/core';
import { Alert, Button, Field, Input, Skeleton, useToast } from '@clientma/ui';

import { providerApi, providerLogin, type ProviderOperator } from './api';
import Overview from './screens/Overview';
import Countries from './screens/Countries';
import Payments from './screens/Payments';
import Plans from './screens/Plans';
import Renewals from './screens/Renewals';
import Reports from './screens/Reports';
import Feedback from './screens/Feedback';
import AiKeys from './screens/AiKeys';
import Tenants from './screens/Tenants';
import './provider.css';

type View =
  | 'overview'
  | 'tenants'
  | 'plans'
  | 'renewals'
  | 'payments'
  | 'reports'
  | 'feedback'
  | 'countries'
  | 'ai';

const TOKEN_KEY = 'clientma.provider.tokens';

/**
 * The seeded operator, from `ProviderOperatorSeeder`.
 *
 * Gated on the build mode below so it can never ship: a production bundle does not contain the
 * block that renders it. Mirrors how the tenant sign-in offers its demo account.
 */
const SEEDED_EMAIL = 'operator@sjstech.example';
const SEEDED_PASSWORD = 'ClientMa-Provider-2026!';

/**
 * The provider console.
 *
 * Deliberately does not use the shared SessionProvider. That is built around a tenant session —
 * a tenant, a company, a permission set — and an operator has none of those: they authenticate
 * against a different endpoint and are authorised by a claim the server checks per call. Reusing
 * the tenant session here would mean pretending an operator is a member of somebody's tenant,
 * and every screen would then have to remember that the pretence is not true.
 *
 * Navigation is local state. Three destinations, no deep links worth honouring.
 */
export default function ProviderApp() {
  const [tokens, setTokens] = useState<AuthTokens | null>(readStoredTokens);
  const [operator, setOperator] = useState<ProviderOperator | null>(null);
  const [checking, setChecking] = useState(true);
  const [view, setView] = useState<View>('overview');

  const signOut = useCallback(() => {
    window.localStorage.removeItem(TOKEN_KEY);
    setTokens(null);
    setOperator(null);
  }, []);

  const api = useMemo(
    () =>
      new ApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
        getTokens: () => readStoredTokens(),
        setTokens: (next) => {
          if (next) window.localStorage.setItem(TOKEN_KEY, JSON.stringify(next));
          else window.localStorage.removeItem(TOKEN_KEY);

          setTokens(next);
        },
        onSessionExpired: signOut,
      }),
    [signOut],
  );

  // A stored token proves nothing on its own — it may be expired, or belong to somebody whose
  // operator access has since been withdrawn. The console asks the server who it is talking to
  // before it shows anything.
  useEffect(() => {
    if (!tokens) {
      setChecking(false);
      return;
    }

    let cancelled = false;

    providerApi(api)
      .whoAmI()
      .then((who) => {
        if (!cancelled) setOperator(who);
      })
      .catch(() => {
        if (!cancelled) signOut();
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, tokens, signOut]);

  if (checking) {
    return (
      <div className="provider-centre">
        <Skeleton height={180} />
      </div>
    );
  }

  if (!operator) {
    return (
      <SignIn
        onSignedIn={(issued, who) => {
          window.localStorage.setItem(TOKEN_KEY, JSON.stringify(issued));
          setTokens(issued);
          setOperator(who);
          setChecking(false);
        }}
        api={api}
      />
    );
  }

  const tabs: { key: View; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'tenants', label: 'Customers' },
    { key: 'plans', label: 'Plans' },
    { key: 'renewals', label: 'Renewals' },
    { key: 'payments', label: 'Payments' },
    { key: 'reports', label: 'Reports' },
    { key: 'feedback', label: 'Feedback' },
    { key: 'countries', label: 'Countries' },
    { key: 'ai', label: 'AI' },
  ];

  return (
    <div className="provider">
      <header className="provider-header">
        <div>
          <div className="provider-header__mark">ClientMa</div>
          <div className="provider-header__sub">Provider console</div>
        </div>

        <div className="provider-header__right">
          <span className="provider-header__who">{operator.displayName}</span>
          <Button size="sm" variant="ghost" onClick={signOut}>
            Sign out
          </Button>
        </div>
      </header>

      <nav className="provider-tabs" aria-label="Sections">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`provider-tab${view === tab.key ? ' provider-tab--active' : ''}`}
            aria-current={view === tab.key ? 'page' : undefined}
            onClick={() => setView(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <main className="provider-main">
        {view === 'overview' && <Overview api={api} onOpenPayments={() => setView('payments')} />}
        {view === 'tenants' && <Tenants api={api} />}
        {view === 'plans' && <Plans api={api} />}
        {view === 'renewals' && <Renewals api={api} />}
        {view === 'payments' && <Payments api={api} />}
        {view === 'reports' && <Reports api={api} />}
        {view === 'feedback' && <Feedback api={api} />}
        {view === 'countries' && <Countries api={api} />}
        {view === 'ai' && <AiKeys api={api} />}
      </main>
    </div>
  );
}

function SignIn({
  api,
  onSignedIn,
}: {
  api: ApiClient;
  onSignedIn: (tokens: AuthTokens, operator: ProviderOperator) => void;
}) {
  const toast = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);

    try {
      const result = await providerLogin(api, email.trim(), password);
      onSignedIn(result.tokens, result.operator);
      toast.success(`Signed in as ${result.operator.displayName}`);
    } catch (failure) {
      // A console that cannot reach its API says the credentials were wrong, and the person
      // retypes a correct password until they doubt it. Whether the server answered at all is
      // not a secret, so that case is told apart from a rejection.
      if (failure instanceof ApiError && failure.status === 0) {
        setError(failure.message);
      } else if (failure instanceof ApiError && failure.status >= 500) {
        setError('The server could not complete the sign-in. Try again in a moment.');
      } else {
        // For an actual rejection the server answers identically for an unknown address, a
        // wrong password and a real account that is not an operator. Saying more here would
        // undo that on the client.
        setError('Those details were not accepted.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="provider-centre">
      <form
        className="provider-signin"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="provider-signin__mark">ClientMa</div>
        <h1 className="provider-signin__title">Provider console</h1>
        <p className="provider-signin__blurb">
          For SJS Tech staff. A customer sign-in will not open this.
        </p>

        {error && <Alert tone="danger">{error}</Alert>}

        <Field label="Email">
          {(id) => (
            <Input
              id={id}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoFocus
            />
          )}
        </Field>

        <Field label="Password">
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </Field>

        <Button type="submit" variant="primary" isLoading={busy}>
          Sign in
        </Button>

        {import.meta.env.DEV && (
          <div className="provider-seeded">
            <div className="provider-actionrow">
              <span className="provider-seeded__label">Seeded operator</span>

              <Button
                size="sm"
                variant="ghost"
                type="button"
                onClick={() => {
                  setEmail(SEEDED_EMAIL);
                  setPassword(SEEDED_PASSWORD);
                }}
              >
                Fill in
              </Button>
            </div>

            <dl className="provider-seeded__list">
              <dt>Email</dt>
              <dd>
                <code>{SEEDED_EMAIL}</code>
              </dd>

              <dt>Password</dt>
              <dd>
                <code>{SEEDED_PASSWORD}</code>
              </dd>
            </dl>
          </div>
        )}
      </form>
    </div>
  );
}

function readStoredTokens(): AuthTokens | null {
  try {
    const raw = window.localStorage.getItem(TOKEN_KEY);
    return raw ? (JSON.parse(raw) as AuthTokens) : null;
  } catch {
    return null;
  }
}
